import { animate, group, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, HostListener, OnInit, Optional, TemplateRef, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ThemeService } from '../_helpers/theming-engine/theme.service';
import { PlacementStudentV2 } from '../_models';
import { ClientListForAddStudentResponse } from '../_models/clients/client-list-for-add-student-response.model';
import { ClientListing } from '../_models/clients/client-listing.model';
import { SearchPlacementRequest } from '../_models/placement-search/search-placement-request.model';
import { SearchPlacementResult } from '../_models/placement-search/search-placement-result.model';
import { ClientService, PlacementService, SessionService } from '../_services';
import { FeatureHelperService } from '../_helpers/feature-helper/feature-helper.service';
import { SessionOverviewHelperService } from '../_helpers/session-overview-helper/session-overview-helper.service';
import * as CryptoJS from 'crypto-js';
import { OverviewEncryptedPayload } from '../_models/session-overview/overview-encrypted-payload.model';
import { SessionUserHashManagerService } from '../_services/session-user-hash-manager/session-user-hash-manager.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Guid } from 'guid-typescript';
import { CookieService } from 'ngx-cookie-service';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { AuthenticationStateHelperService } from '../_helpers/authentication-state-helper/authentication-state-helper.service';
import { SuccessModalHelperService } from '../_helpers/success-modal-helper/success-modal-helper.service';
import { FeatureSwitchService } from '../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../_constants/feature-switch-keys';
import { DELIVERY_MODE_OPTIONS, ORDER_TYPE_OPTIONS } from '../_constants/search-filter-options';
import { OverviewClientFilters } from '../_models/session-overview/overview-encrypted-payload.model';
import { FilterSpec, FilterState } from '../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../shared/filter-ux/screen-filter-ux';
import { DatePreset, isDatePreset, resolveDatePreset } from '../shared/filter-ux/date-presets';
import { DatePresetSelection } from '../shared/filter-ux/date-presets.component';

/**
 * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
 * only: free text (search text, booking identifier) stays out of the URL (browser history, telemetry)
 * and browser storage.
 */
const FILTER_SPEC: FilterSpec = {
  client: 'number', brand: 'number[]', delivery: 'string', orderType: 'string', ended: 'boolean',
  range: 'string', from: 'string', to: 'string', preset: 'string'
};
const DATE_RANGES = ['today', 'tomorrow', 'lastWeek', 'thisWeek', 'custom'];

@Component({
    selector: 'app-session-overview',
    templateUrl: './session-overview.component.html',
    styleUrls: ['./session-overview.component.css'],
    animations: [
        trigger('openCloseSlider', [
            transition(':leave', [
                style({ opacity: 1 }),
                group([
                    animate('.4s .2s ease-out', style({ opacity: 0 })),
                ]),
            ]),
            transition(':enter', [
                style({ opacity: 0 }),
                group([
                    // animate('.1s .4s', style({width: '100px'})),
                    animate('.4s .8s ease-in', style({ opacity: 1 })),
                ]),
            ]),
        ]),
    ],
    standalone: false
})
export class SessionOverviewComponent implements OnInit, AfterViewInit {
  @ViewChild('utilityBarForm', { static: true }) utilityBarForm: TemplateRef<any>;
  

  public selectedClient: ClientListing;
  public clientDisabled = (item: any): boolean => item?.clientId == 0;
  public bookingId: string;
  public searchTerm: string;
  public showEnded: boolean = false;
  public clientIds: Array<number> = new Array<number>();
  /** Selected brands; empty means every brand the user may see. */
  public companyIds: Array<number> = new Array<number>();

  /** For the template's `*appFeatureSwitch`. */
  readonly featureKeys = FeatureSwitchKeys;
  readonly deliveryModeOptions = DELIVERY_MODE_OPTIONS;
  readonly orderTypeOptions = ORDER_TYPE_OPTIONS;
  /**
   * The filters.session-overview switch. Off (the default, and on any failure): the Brand, Delivery
   * and Order type filters don't show, companyIds is not sent and no client-side filter applies.
   */
  public filtersEnabled: boolean = false;
  /** Delivery and Order type, applied client-side to the returned placements on each search. */
  public clientFilters: OverviewClientFilters = new OverviewClientFilters();
  /** The date preset behind a custom range, or null. With the switch on, the presets replace the week buttons. */
  public datePreset: DatePreset | null = null;
  /** Remembered filters and filter state in the URL; used only while filters.session-overview is on. */
  private filterUx: ScreenFilterUx;
  /** Filters to restore once both the switch and the client list have arrived. */
  private pendingFilterState: FilterState = null;
  private clientsLoaded: boolean = false;
  public searchTextPlaceholder: string = "";

  public enhancedSearchPermission: boolean = true;

  public selectedFilterDateRange: string = "";
  // Bound to native <input type="date"> via ngModel, which reads/writes yyyy-MM-dd STRINGS —
  // a Date here leaves the input blank and later breaks getFilterDates ('custom') with
  // "setHours is not a function". ISO strings also compare correctly in validateDates().
  // Defaults: from = one month ago, to = today (the request widens "to" to end of day).
  public fromDate: string = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10);
  public toDate: string = new Date().toISOString().slice(0, 10);
  public fromDateAfterToDate: boolean = false;

  public clientList: Array<ClientListing> = new Array<ClientListing>();
  public filteredClientList: Array<ClientListing> = new Array<ClientListing>();
  public showClientAccessError: boolean = false;
  public showClientValidationError: boolean = false;

  public viewBothSliders: boolean = false;

  public initialSearchCompleted: boolean = false;
  public searchExpanded: boolean = true;

  constructor(
    public themeService: ThemeService,
    private placementService: PlacementService,
    private successModalHelper: SuccessModalHelperService,
    private featureHelperService: FeatureHelperService,
    private clientService: ClientService,
    private sessionService: SessionService,
    private sessionOverviewHelper: SessionOverviewHelperService,
    private userHasHelper: SessionUserHashManagerService,
    private router: Router,
    private cookieService: CookieService,
    private errorModalHelper: ErrorModalHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService,
    private featureSwitches: FeatureSwitchService,
    @Optional() private route: ActivatedRoute = null
    ) { }

  ngAfterViewInit(): void {
    
  }

  ngOnInit() {
    
    let featureList = new Array<string>();
    featureList.push(
      "SessionOverviewView",
      "BookingSetupView",
      "TutorReportsView",
      "SessionOverviewEnhancedSearch");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);
    
    if (!currentUserFeatures.filter(f => f.feature === "SessionOverviewView")[0].enabled) {
      console.error("User does not have the required permissions to view the overview screen");
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname }});
      this.authenticationStateHelper.unauthorizedToken = true;
    }

    this.enhancedSearchPermission = currentUserFeatures.find(f => f.feature == "SessionOverviewEnhancedSearch").enabled;

    this.filterUx = new ScreenFilterUx(FeatureSwitchKeys.filtersSessionOverview, FILTER_SPEC, this.router, this.route);
    this.featureSwitches.isEnabled(FeatureSwitchKeys.filtersSessionOverview).subscribe(enabled => {
      this.filtersEnabled = enabled;
      // A shared link's filters, else this user's last ones. Read only once the switch is known to be on.
      this.pendingFilterState = enabled ? this.filterUx.initialState() : null;
      this.applyPendingFilterState();
    });
    // this.enhancedSearchPermission = false;

    if (this.enhancedSearchPermission) {
      this.selectedFilterDateRange = "today";
      this.populateClientDropdown();
    }
    
    setTimeout(() => {
      this.searchTextPlaceholder = "eg. Session / Subject / Learner";
    }, 500);
  }

  public searchPlacements(): void {

    if (this.enhancedSearchPermission && (this.selectedClient == null || this.selectedClient.clientId == 0)) {
      this.showClientValidationError = true;
      return;
    }

    this.showClientValidationError = false;
    this.initialSearchCompleted = true;
    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }

    this.sessionOverviewHelper.sessions = new Array<SearchPlacementResult>(); 
    this.sessionOverviewHelper.sessionsFilterHolder = new Array<SearchPlacementResult>(); 
    this.sessionOverviewHelper.sessionChangedWatchTrigger();

    forkJoin([this.searchPlacementsAsync()])
    .subscribe(response => {
    }, error => {
      this.errorModalHelper.show(error.message, error);
    });
  }

  public searchPlacementsAsync(): Promise<any> {
    return new Promise((resolve: any, reject: any) => {
      let request = new SearchPlacementRequest();
      request.bookingId = this.bookingId == null ? "" : this.bookingId;
      request.clientName = "";
      request.searchTerm = this.searchTerm;
      request.pendingSetupOnly = false;
      request.showEnded = this.showEnded;
      request.clientIds = this.selectedClient == null ? [] : [this.selectedClient.clientId];
      // New parameters only when the switch is on; off, the request is the pre-filter shape.
      if (this.filtersEnabled) {
        request.companyIds = this.companyIds ?? [];
      }
      request.attachSessions = true;
      request.attachStudents = true;

      let filterDates = this.getFilterDates();

      request.dateFrom = filterDates.fromDate;
      request.dateTo = filterDates.toDate;

      this.placementService.searchPlacements(request).subscribe((response: SearchPlacementResult[]) => {

        if (response == null) {
          this.sessionOverviewHelper.sessions = new Array<SearchPlacementResult>(); 
          this.sessionOverviewHelper.sessionsFilterHolder = new Array<SearchPlacementResult>(); 
          this.searchExpanded = true;
        } else {
          const shown = this.filtersEnabled
            ? this.sessionOverviewHelper.applyClientFilters(response, this.clientFilters)
            : response;
          this.sessionOverviewHelper.sessions = shown;
          this.sessionOverviewHelper.sessionsFilterHolder = shown;
          this.searchExpanded = false;
        }

        this.sessionOverviewHelper.sessions.forEach(session => {
          let emptySpaceCount = session.maxGroupSize - session.students.length;
          if (Math.sign(emptySpaceCount) != -1) {
            for (let i = 0; i < emptySpaceCount; i++) {
              let emptyStudent = new PlacementStudentV2();
              emptyStudent.studentFirstName = "Unassigned Slot";
              emptyStudent.studentLastName = "";
              emptyStudent.studentId = 0;
              session.students.push(emptyStudent);
            }
          }
          if (this.sessionOverviewHelper.mobileOverwrite) {
            session.showInfo = true;
            session.showSessionSlider = false;
          } else {
            session.showInfo = false;
            session.showSessionSlider = false;
          }
        });
        
        this.sessionOverviewHelper.sessionChangedWatchTrigger();
        resolve("Complete");

      }, error => {
        reject(error);
      });
    });
        
  }

  /** A preset is a custom range with a name; like the other range buttons, choosing one searches. */
  public onDatePreset(selection: DatePresetSelection): void {
    this.datePreset = selection.preset;
    this.selectedFilterDateRange = 'custom';
    this.fromDate = selection.from;
    this.toDate = selection.to;
    this.validateDates();
    this.searchPlacements();
  }

  /** Clear all: back to the defaults, forgotten, and out of the URL. */
  public clearAll(): void {
    this.selectedClient = null;
    this.companyIds = [];
    this.clientFilters = new OverviewClientFilters();
    this.showEnded = false;
    this.searchTerm = null;
    this.bookingId = null;
    this.selectedFilterDateRange = 'today';
    this.fromDate = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10);
    this.toDate = new Date().toISOString().slice(0, 10);
    this.fromDateAfterToDate = false;
    this.datePreset = null;
    this.filterUx.clear();
  }

  /** A preset is remembered as the preset, so it resolves afresh on the next visit. */
  private filterState(): FilterState {
    const custom = this.selectedFilterDateRange === 'custom' && !this.datePreset;
    return {
      client: this.selectedClient?.clientId || null,
      brand: this.companyIds,
      delivery: this.clientFilters?.deliveryMode,
      orderType: this.clientFilters?.provisionType,
      ended: this.showEnded || null,
      range: this.datePreset ? null : this.selectedFilterDateRange,
      preset: this.datePreset,
      from: custom ? this.fromDate : null,
      to: custom ? this.toDate : null
    };
  }

  /**
   * Restores remembered or linked filters. The client is an object from the client list, so with the
   * enhanced search this waits for the list, then the overview loads as it would after Search.
   */
  private applyPendingFilterState(): void {
    const state = this.pendingFilterState;
    if (!state || (this.enhancedSearchPermission && !this.clientsLoaded)) {
      return;
    }
    this.pendingFilterState = null;

    this.companyIds = (state.brand as number[]) ?? [];
    this.clientFilters = new OverviewClientFilters();
    this.clientFilters.deliveryMode = (state.delivery as string) ?? null;
    this.clientFilters.provisionType = (state.orderType as string) ?? null;
    this.showEnded = state.ended === true;
    this.datePreset = isDatePreset(state.preset) ? state.preset : null;
    if (this.datePreset) {
      const range = resolveDatePreset(this.datePreset);
      this.selectedFilterDateRange = 'custom';
      this.fromDate = range.from;
      this.toDate = range.to;
    } else if (DATE_RANGES.includes(state.range as string)) {
      this.selectedFilterDateRange = state.range as string;
      if (this.selectedFilterDateRange === 'custom') {
        this.fromDate = (state.from as string) ?? this.fromDate;
        this.toDate = (state.to as string) ?? this.toDate;
      }
    }
    this.validateDates();
    const client = this.clientList.find(c => c.clientId === state.client);
    if (client) {
      this.selectedClient = client;
    }
    if (client || !this.enhancedSearchPermission) {
      this.searchPlacements();
    }
  }

  /** A Brand filter change - applied on the next search. */
  public brandsChanged(companyIds: number[]): void {
    this.companyIds = companyIds ?? [];
  }

  public validateDates(): void {
    if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
      this.fromDateAfterToDate = true;
    } else {
      this.fromDateAfterToDate = false;
    }
  }

  public filterDateRangeChange(dateFilter: string): void {
    this.datePreset = null;
    let tempHolder = this.selectedFilterDateRange;
    this.selectedFilterDateRange = dateFilter;
    if (dateFilter != 'custom') {
      this.searchPlacements();
      if (this.showClientValidationError) {
        this.selectedFilterDateRange = tempHolder;
      }
    }
  }

  public togglePlacementFullscreen(toggle: boolean): void {
    if (toggle) {
      let request = new SearchPlacementRequest();
      request.bookingId = this.bookingId == null ? "" : this.bookingId;
      request.clientName = "";
      request.searchTerm = this.searchTerm;
      request.pendingSetupOnly = false;
      request.showEnded = this.showEnded;
      request.clientIds = this.selectedClient == null ? [] : [this.selectedClient.clientId];
      // New parameters only when the switch is on; off, the request is the pre-filter shape.
      if (this.filtersEnabled) {
        request.companyIds = this.companyIds ?? [];
      }
      request.attachSessions = true;
      request.attachStudents = true;

      let filterDates = this.getFilterDates();

      request.dateFrom = filterDates.fromDate;
      request.dateTo = filterDates.toDate;

      let payload = new OverviewEncryptedPayload();
      payload.request = request;
      payload.userHash = this.userHasHelper.getCurrentSessionUserHash();
      if (this.filtersEnabled) {
        payload.filters = { ...this.clientFilters };
      }

      let encryptedPayload = this.encryptFullscreenPayload(payload);

      let guid = Guid.create();
      let expiryDate = new Date();
      expiryDate.setHours(expiryDate.getHours() + 2);
      this.cookieService.set(guid.toString(), encryptedPayload, expiryDate, '/', null, null, 'Lax');

      const parsedUrl = new URL(window.location.href);
      let urlPath = parsedUrl.origin;
      urlPath += urlPath.endsWith("/") ? "overview-fullscreen/" + guid : "/overview-fullscreen/" + guid;

      setTimeout(() => {
         window.open(urlPath, "_blank");
      }, 100);
      
    }
  }

  private populateClientDropdown(cancelDisableLoading: boolean = false): void {
      
      this.clientService.getClientListForAddStudent().subscribe((response: ClientListForAddStudentResponse) => {
        if (response.success) {
          this.clientList = response.clients;
          this.filteredClientList = response.clients;
          this.clientsLoaded = true;
          this.applyPendingFilterState();
          // this.clientList.splice(0, 0, {clientId: 0, clientName: "--- Select Client ---"});
          // this.selectedClient = this.clientList.filter(c => c.clientId == 0)[0];
        } else if (response.responseMessage.toLowerCase() == "no valid client roles assigned") {
          this.showClientAccessError = true;
        } else {
          this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", {isValidationError: false});
        }
        
        
    }, error => {
      console.error(error);
      this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
    });
  }

  private getFilterDates(): any {
    var today = new Date();
    today.setHours(2, 0, 0, 0);
    this.sessionService.getSessionTileDates(new Date());
    
    switch (this.selectedFilterDateRange) {
      case 'today':
        return {
          fromDate: today,
          toDate: today
        };
    
      case 'tomorrow':
        let tomorrow = new Date(today.setDate(today.getDate() + 1));
        return {
          fromDate: tomorrow,
          toDate: tomorrow
        };
    
      case 'lastWeek':
        return {
          fromDate: this.sessionService.sessionDataParamLastWeek.startDate,
          toDate: this.sessionService.sessionDataParamLastWeek.endDate
        };
    
      case 'thisWeek':
        return {
          fromDate: this.sessionService.sessionDataParamThisWeek.startDate,
          toDate: this.sessionService.sessionDataParamThisWeek.endDate
        };
    
      case 'custom': {
        // fromDate/toDate are yyyy-MM-dd strings from the native date inputs.
        const customFrom = new Date(this.fromDate);
        customFrom.setHours(6, 0, 0, 0);
        // End of the chosen day — a "to" of today must include today's later sessions.
        const customTo = new Date(this.toDate);
        customTo.setHours(23, 59, 59, 999);
        return {
          fromDate: customFrom,
          toDate: customTo
        };
      }
    
      default:
        break;
    }
  }

  private encryptFullscreenPayload(item: OverviewEncryptedPayload): string {
    var iv = CryptoJS.enc.Hex.parse("e84ad660c4721ae0e84ad660c4721ae0");
    let user = JSON.parse(localStorage.getItem('currentUser'));

    var Pass = CryptoJS.enc.Utf8.parse(user.firstName);

    var Salt = CryptoJS.enc.Utf8.parse(user.username);

    var key128Bits1000Iterations = CryptoJS.PBKDF2(Pass.toString(CryptoJS.enc.Utf8), Salt, { keySize: 128 / 32, iterations: 1000 });

    return CryptoJS.AES.encrypt(JSON.stringify(item), key128Bits1000Iterations, { mode: CryptoJS.mode.CBC, iv: iv, padding: CryptoJS.pad.Pkcs7 }).toString();// + "=" + key;
  }

}
